/**
 * useKeyboardShortcuts - Global keyboard shortcuts for panel operations
 *
 * BYK-162 & BYK-166: Keyboard shortcuts for panel operations
 *
 * Shortcuts:
 * - Cmd+\ : Toggle left sidebar
 * - Cmd+Shift+\ : Toggle right sidebar
 * - Cmd+K : Command palette (future)
 * - Cmd+1 : Focus left sidebar
 * - Cmd+2 : Focus canvas
 * - Cmd+3 : Focus right sidebar
 * - Escape : Return focus to canvas
 */

import { useEffect, useCallback } from "react";
import { useUIStore, type LayoutPreset } from "@/stores/uiStore";

interface KeyboardShortcutsOptions {
  /** Enable/disable shortcuts */
  enabled?: boolean;
  /** Callback when command palette is triggered */
  onCommandPalette?: () => void;
  /** Callback when help modal is triggered */
  onShowHelp?: () => void;
}

export function useKeyboardShortcuts(options: KeyboardShortcutsOptions = {}) {
  const { enabled = true, onCommandPalette, onShowHelp } = options;

  const {
    toggleLeftSidebar,
    toggleRightSidebar,
    setFocusedPanel,
    applyPreset,
    focusCanvas,
  } = useUIStore();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Ignore shortcuts when typing in inputs
      const target = event.target as HTMLElement;
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable
      ) {
        // Only allow Escape to exit input
        if (event.key === "Escape") {
          target.blur();
        }
        return;
      }

      const isMeta = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;

      // Cmd+\ - Toggle left sidebar
      if (isMeta && event.key === "\\") {
        event.preventDefault();
        if (isShift) {
          toggleRightSidebar();
        } else {
          toggleLeftSidebar();
        }
        return;
      }

      // Cmd+K - Command palette
      if (isMeta && event.key === "k") {
        event.preventDefault();
        onCommandPalette?.();
        return;
      }

      // Cmd+? or Cmd+/ - Show help modal
      if (isMeta && (event.key === "?" || (isShift && event.key === "/"))) {
        event.preventDefault();
        onShowHelp?.();
        return;
      }

      // Cmd+1/2/3 - Focus panels
      if (isMeta && event.key === "1") {
        event.preventDefault();
        setFocusedPanel("left");
        return;
      }
      if (isMeta && event.key === "2") {
        event.preventDefault();
        setFocusedPanel("center");
        return;
      }
      if (isMeta && event.key === "3") {
        event.preventDefault();
        setFocusedPanel("right");
        return;
      }

      // Cmd+Shift+1-5 - Layout presets
      if (isMeta && isShift) {
        const presetMap: Record<string, LayoutPreset> = {
          "1": "full-canvas",
          "2": "design-mode",
          "3": "properties-mode",
          "4": "split-view",
          "5": "ai-focus",
        };
        if (presetMap[event.key]) {
          event.preventDefault();
          applyPreset(presetMap[event.key]);
          return;
        }
      }

      // Escape - Focus canvas
      if (event.key === "Escape") {
        event.preventDefault();
        focusCanvas();
        return;
      }
    },
    [
      enabled,
      toggleLeftSidebar,
      toggleRightSidebar,
      setFocusedPanel,
      applyPreset,
      focusCanvas,
      onCommandPalette,
      onShowHelp,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}

/**
 * Formatted shortcut display for UI
 */
export function formatShortcut(keys: string[]): string {
  const isMac = navigator.platform.toLowerCase().includes("mac");
  return keys
    .map((key) => {
      switch (key) {
        case "meta":
          return isMac ? "⌘" : "Ctrl";
        case "shift":
          return isMac ? "⇧" : "Shift";
        case "alt":
          return isMac ? "⌥" : "Alt";
        case "\\":
          return "\\";
        default:
          return key.toUpperCase();
      }
    })
    .join(isMac ? "" : "+");
}

/**
 * Keyboard shortcut definitions for help UI
 */
export const KEYBOARD_SHORTCUTS = [
  // View mode shortcuts
  { keys: ["["], description: "Previous view mode (2D ← Split ← 3D)" },
  { keys: ["]"], description: "Next view mode (2D → Split → 3D)" },
  // Sidebar shortcuts
  { keys: ["meta", "\\"], description: "Toggle left sidebar" },
  { keys: ["meta", "shift", "\\"], description: "Toggle right sidebar" },
  { keys: ["meta", "k"], description: "Open command palette" },
  { keys: ["meta", "?"], description: "Show keyboard shortcuts" },
  { keys: ["meta", "1"], description: "Focus left sidebar" },
  { keys: ["meta", "2"], description: "Focus canvas" },
  { keys: ["meta", "3"], description: "Focus right sidebar" },
  { keys: ["meta", "shift", "1"], description: "Full canvas mode" },
  { keys: ["meta", "shift", "2"], description: "Design mode" },
  { keys: ["meta", "shift", "3"], description: "Properties mode" },
  { keys: ["meta", "shift", "4"], description: "Split view" },
  { keys: ["meta", "shift", "5"], description: "AI focus mode" },
  { keys: ["Escape"], description: "Return focus to canvas" },
] as const;

export default useKeyboardShortcuts;
